import { useEffect } from "react";
import confetti from "canvas-confetti";

const COLORS = ["#2f6bff", "#19a463", "#e0931a", "#d23b78", "#8b5cf6", "#17b8c4", "#ffd23f", "#ff5a7a"];

/**
 * One-shot confetti burst launching from both bottom corners of the actual
 * viewport — like party-popper cannons firing inward from each side.
 * canvas-confetti draws onto its own <canvas> appended straight to
 * document.body with position: fixed, so (unlike a DOM overlay nested inside
 * the caller's markup) it's never confined by an ancestor's backdrop-filter
 * or transform — it always launches from the real screen corners, no matter
 * which card this is rendered inside of.
 *
 * @param {object} props
 * @param {() => void} [props.onDone] called once both bursts finish.
 * @param {number} [props.pieces=60] confetti pieces PER corner.
 * @param {number} [props.durationMs=2200] fallback timeout if the animation
 *   promise never resolves (belt-and-braces; should normally resolve on its own).
 */
export default function CornerConfetti({ onDone, pieces = 60, durationMs = 2200 }) {
  useEffect(() => {
    let fired = false;
    const finish = () => {
      if (fired) return;
      fired = true;
      onDone?.();
    };

    const burst = (originX, angle) =>
      confetti({
        particleCount: pieces,
        angle,
        spread: 70,
        startVelocity: 45,
        origin: { x: originX, y: 1 },
        colors: COLORS,
        zIndex: 9000,
        disableForReducedMotion: true,
      });

    Promise.all([
      burst(0, 60), // bottom-left corner, firing up-and-right
      burst(1, 120), // bottom-right corner, firing up-and-left
    ]).then(finish);

    const fallback = setTimeout(finish, durationMs + 500);

    return () => {
      fired = true;
      clearTimeout(fallback);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
